/**
 * Derives anomaly objects from trace steps. Each anomaly must include rationale (message + related steps).
 *
 * @param {object} params
 * @param {Array<object>} params.steps
 * @param {object} [params.summary]
 * @returns {Array<{ code: string, severity: 'warning'|'error'|'info', message: string, relatedStepKeys: string[] }>}
 */
export function detectPriceTraceAnomalies({ steps, summary }) {
  const list = Array.isArray(steps) ? steps : [];
  const byKey = Object.fromEntries(list.map((s) => [s.key, s]));
  const out = [];

  const fxSteps = list.filter((s) => s.layer === "currency" && s.field?.includes("USD"));
  if (fxSteps.length >= 2) {
    out.push({
      code: "double_conversion",
      severity: "warning",
      message:
        "נמצאו שני שלבים או יותר של המרת מטבע (USD→ILS). ייתכן כפל המרה אם אחד מהם מיותר.",
      relatedStepKeys: fxSteps.map((s) => s.key),
    });
  }

  const commissionLayers = list.filter((s) => s.layer === "commission" && s.field === "stack");
  if (commissionLayers.length > 1) {
    out.push({
      code: "double_commission",
      severity: "error",
      message: "מספר שכבות עמלה מלאות — ייתכל חישוב כפול של עמלה.",
      relatedStepKeys: commissionLayers.map((s) => s.key),
    });
  }

  const roomStep = list.find((s) => s.field === "room_quotes_vs_search_rooms");
  if (roomStep?.notes?.includes("partial_sum") && roomStep?.rawValue === "mismatch") {
    out.push({
      code: "room_multiplier_applied",
      severity: "info",
      message:
        "מספר הצעות חדר ב־composite אינו תואם למספר החדרים בחיפוש — נעשה שימוש בסכום חלקי של חדרים זמינים.",
      relatedStepKeys: [roomStep.key].filter(Boolean),
    });
  }

  const paxStep = list.find((s) => s.field === "passenger_hint");
  if (paxStep && summary?.rooms > 1) {
    out.push({
      code: "passenger_multiplier_applied",
      severity: "info",
      message:
        "חיפוש מרובה חדרים — מחיר ה־composite בדרך כלל לסט החדרים; השוואה פר־נוסע דורשת פירוק נוסף.",
      relatedStepKeys: [paxStep.key].filter(Boolean),
    });
  }

  const finalStep = list.filter((s) => s.layer === "display").pop();
  if (
    finalStep &&
    summary?.finalDisplay != null &&
    finalStep.afterRound != null &&
    Math.abs(Number(finalStep.afterRound) - Number(summary.finalDisplay)) > 0.02
  ) {
    out.push({
      code: "display_mismatch",
      severity: "error",
      message: "המחיר בשורת התצוגה הסופית אינו תואם לסיכום העליון — בדוק עיגול או שינוי אחרי בניית ה־trace.",
      relatedStepKeys: [finalStep.key].filter(Boolean),
    });
  }

  const strategyStep = list.find((s) => s.field === "price_source_strategy");
  const compositeStep = list.find((s) => s.field === "composite_total_price");
  const compositeValid =
    compositeStep?.normalized != null &&
    Number.isFinite(Number(compositeStep.normalized)) &&
    Number(compositeStep.normalized) > 0;
  if (strategyStep?.normalized === "fallback" && compositeValid) {
    out.push({
      code: "pricing_strategy_mismatch",
      severity: "info",
      message:
        "נבחר LowestPackagePrice למרות ש־composite_total_price בטבלה תקף — חשד לפער בלוגיקת בחירת המחיר.",
      relatedStepKeys: [strategyStep.key, compositeStep.key].filter(Boolean),
    });
  }

  const unknownStep = list.find((s) => s.notes?.includes("unlabeled_jump"));
  if (unknownStep) {
    out.push({
      code: "unknown_transform",
      severity: "warning",
      message: unknownStep.notes || "זוהה שינוי מחיר בלי תיאור שכבה ברור.",
      relatedStepKeys: [unknownStep.key].filter(Boolean),
    });
  }

  return dedupeAnomalies(out, byKey);
}

/**
 * חריגות ייעודיות ל־trace חבילה (LDS): פירוק מחיר, חבילה דינמית, כפל ספירה.
 *
 * @param {object} params
 * @param {Array<object>} params.steps
 * @param {object} [params.summary]
 * @param {object} [params.breakdown] — תוצאת extractPackagePriceBreakdown
 * @param {object} [params.searchContext] — { searchTab, isDynamicPackageEstimate, ... }
 */
export function detectPackagePriceTraceAnomalies({
  steps,
  summary,
  breakdown,
  searchContext = {},
}) {
  const list = Array.isArray(steps) ? steps : [];
  const byKey = Object.fromEntries(list.map((s) => [s.key, s]));
  const out = [];
  const br = breakdown || {};
  const EPS = 0.02;

  const keysFor = (field) => {
    const s = list.find((x) => x.field === field);
    return s?.key ? [s.key] : [];
  };

  const f = br.flight;
  const h = br.hotel;
  const e = br.extras;
  const t = br.total;

  if (f != null && h != null && t != null) {
    const sum = (f || 0) + (h || 0) + (e || 0);
    if (Math.abs(sum - t) > EPS) {
      out.push({
        code: "component_mismatch",
        severity: "warning",
        message:
          "סכום טיסה+מלון (+אקסטרות אם קיימות) במטבע המקור אינו תואם ל־total של החבילה.",
        relatedStepKeys: [
          ...keysFor("flight_price_component"),
          ...keysFor("hotel_price_component"),
          ...keysFor("extras_component"),
          ...keysFor("package_base_price"),
        ].filter(Boolean),
      });
    }
  }

  if (t != null && (f == null || h == null)) {
    out.push({
      code: "missing_component",
      severity: "info",
      message:
        "חסר פירוק מחיר לטיסה או למלון — קשה לאמת את מבנה החבילה מול המחיר הכולל.",
      relatedStepKeys: [
        ...keysFor("package_base_price"),
        ...(f == null ? keysFor("flight_price_component") : []),
        ...(h == null ? keysFor("hotel_price_component") : []),
      ].filter(Boolean),
    });
  }

  if (f != null && h != null && t != null) {
    const sum = (f || 0) + (h || 0) + (e || 0);
    if (sum > t + EPS) {
      out.push({
        code: "double_count",
        severity: "warning",
        message:
          "סכום הרכיבים גבוה מהמחיר הכולל — ייתכן כפל ספירה (למשל תוספות שכבר כלולות במחיר מלון/טיסה).",
        relatedStepKeys: [
          ...keysFor("flight_price_component"),
          ...keysFor("hotel_price_component"),
          ...keysFor("extras_component"),
          ...keysFor("package_base_price"),
        ].filter(Boolean),
      });
    }
  }

  const dynamicEstimate =
    searchContext?.searchTab === "DynamicPackages" ||
    searchContext?.isDynamicPackageEstimate === true;
  if (dynamicEstimate) {
    out.push({
      code: "dynamic_package_estimate",
      severity: "info",
      message:
        "מצב חבילה דינמית / הערכה — המחיר עלול להשתנות לפני הזמנה סופית; ה־trace משקף חישוב נוכחי בלבד.",
      relatedStepKeys: keysFor("package_base_price"),
    });
  }

  return dedupeAnomalies(out, byKey);
}

function dedupeAnomalies(anomalies, byKey) {
  const seen = new Set();
  return anomalies.filter((a) => {
    const k = `${a.code}:${(a.relatedStepKeys || []).join(",")}`;
    if (seen.has(k)) return false;
    const keys = a.relatedStepKeys || [];
    const valid = keys.every((id) => !id || byKey[id]);
    if (!valid) return false;
    seen.add(k);
    return true;
  });
}
